import React, { createContext, useContext } from 'react'
import type { PlatformUserDomainRecord } from '../../models/domain/platform-user-domain-record'
import type { AccessOwner } from './access-owner'

/** The owner as the app uses it: its domain list is the platform's own record type. */
export type AppAccessOwner = AccessOwner<PlatformUserDomainRecord>

const AccessOwnerContext = createContext<AppAccessOwner | undefined>(undefined)

export type AccessOwnerProviderProps = {
    owner: AppAccessOwner
    children?: React.ReactNode
}

/** Delivers the access owner to the tree below it. The composition root mounts it once, above every consumer. */
export const AccessOwnerProvider = ({ owner, children }: AccessOwnerProviderProps) => (
    <AccessOwnerContext.Provider value={owner}>{children}</AccessOwnerContext.Provider>
)

/**
 * The access owner of the tree. It throws outside an AccessOwnerProvider: falling back to a
 * module-level owner would let a component read an owner nobody delivered to it.
 */
export const useAccessOwner = (): AppAccessOwner => {
    const owner = useContext(AccessOwnerContext)
    if (!owner) {
        throw new Error('useAccessOwner needs an AccessOwnerProvider above it')
    }
    return owner
}
